import React, { useState, useMemo } from 'react';
import { Box, TextField, Button, Stack, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

function LineSorter() {
  const [text, setText] = useState('banana\napple\norange\napple\n\n');

  const stats = useMemo(() => {
    const lines = text.split('\n');
    const totalLines = lines.length;
    const uniqueLines = new Set(lines.filter(Boolean)).size;
    return { totalLines, uniqueLines };
  }, [text]);

  const processLines = (operation) => {
    let lines = text.split('\n');
    switch (operation) {
      case 'sortAsc':
        lines.sort((a, b) => a.localeCompare(b));
        break;
      case 'sortDesc':
        lines.sort((a, b) => b.localeCompare(a));
        break;
      case 'reverse':
        lines.reverse();
        break;
      case 'unique':
        lines = [...new Set(lines)];
        break;
      case 'removeEmpty':
        lines = lines.filter(line => line.trim() !== '');
        break;
      default:
        break;
    }
    setText(lines.join('\n'));
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
  };

  return (
    <>
      <SEO
        title="Line Sorter & Text Manipulator | UmbrellaCo"
        description="Sort text lines alphabetically, reverse order, remove duplicates, and delete empty lines. Free online text processing tool."
        keywords="line sorter, sort text lines, reverse lines, remove duplicate lines, text manipulator"
        url="/text-tools/line-sorter"
        jsonLd={createSoftwareAppSchema(
          'Line Sorter & Text Manipulator',
          'Sort, reverse, and deduplicate lines of text instantly.',
          'https://umbrellaco.in/text-tools/line-sorter',
          ['Text Processing', 'List Sorting', 'Data Cleaning']
        )}
      />
      <ToolPageContainer
        title="Line Operations"
        description="Clean and organize lists by sorting lines alphabetically, reversing order, removing duplicates, or stripping empty lines."
      >

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Button onClick={() => processLines('sortAsc')} startIcon={<NorthIcon />}>Sort Asc</Button>
        <Button onClick={() => processLines('sortDesc')} startIcon={<SouthIcon />}>Sort Desc</Button>
        <Button onClick={() => processLines('reverse')} startIcon={<SwapVertIcon />}>Reverse</Button>
        <Button onClick={() => processLines('unique')} startIcon={<FilterListOffIcon />}>Unique</Button>
        <Button onClick={() => processLines('removeEmpty')} startIcon={<PlaylistRemoveIcon />}>Remove Empty</Button>
      </Stack>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <TextField
          multiline
          value={text}
          onChange={(e) => setText(e.target.value)}
          variant="outlined"
          fullWidth
          sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { height: '100%' }, '& textarea': { height: '100% !important' } }}
        />
        <Stack direction="row" spacing={1} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <Tooltip title="Copy"><IconButton onClick={handleCopy} size="small"><ContentCopyIcon /></IconButton></Tooltip>
          <Tooltip title="Clear"><IconButton onClick={() => setText('')} size="small"><ClearIcon /></IconButton></Tooltip>
        </Stack>
      </Box>

      <Stack direction="row" spacing={2} sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }} justifyContent="center">
        <Typography variant="body2">Total Lines: {stats.totalLines}</Typography>
        <Divider orientation="vertical" flexItem />
        <Typography variant="body2">Unique Lines: {stats.uniqueLines}</Typography>
      </Stack>
      </ToolPageContainer>
    </>
  );
}

export default LineSorter;